import React from 'react';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { FaFire, FaThermometerHalf, FaShieldAlt, FaCheckCircle, FaTools, FaChartLine, FaCog, FaFlask, FaWater, FaIndustry } from 'react-icons/fa';

const BoilerWaterTreatment = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row className="align-items-center">
            <Col lg={8}>
              <h1 className="display-4 fw-bold mb-4">Boiler Water Treatment</h1>
              <p className="lead mb-4">
                Comprehensive boiler water treatment solutions to prevent scale formation, corrosion, 
                and carryover while ensuring optimal steam system performance and efficiency.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Overview */}
      <section className="section-padding">
        <Container>
          <Row className="mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-4">Boiler Water Treatment Importance</h2>
              <p className="lead">
                Proper boiler water treatment is critical for maintaining steam system integrity, 
                preventing equipment damage, and ensuring safe, efficient operation.
              </p>
            </Col>
          </Row>
          
          <Row className="mb-4">
            <Col lg={6}>
              <h4 className="mb-3">Common Boiler Problems:</h4>
              <ul>
                <li>Scale formation on heat transfer surfaces</li>
                <li>Corrosion of boiler tubes and equipment</li>
                <li>Steam carryover and priming</li>
                <li>Foaming and boiler water entrainment</li>
                <li>Reduced heat transfer efficiency</li>
              </ul>
            </Col>
            <Col lg={6}>
              <h4 className="mb-3">Treatment Benefits:</h4>
              <ul>
                <li>Extended boiler lifespan</li>
                <li>Improved fuel efficiency</li>
                <li>Reduced maintenance costs</li>
                <li>Enhanced steam quality</li>
                <li>Prevention of costly downtime</li>
              </ul>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Treatment Programs */}
      <section className="section-padding bg-light">
        <Container>
          <Row className="mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-4">Comprehensive Treatment Programs</h2>
              <p className="lead">Multi-chemical approach for complete boiler protection</p>
            </Col>
          </Row>
          
          <Row>
            <Col md={6} lg={4} className="mb-4">
              <Card className="h-100">
                <Card.Body className="text-center">
                  <div className="feature-icon mb-3"><FaShieldAlt /></div>
                  <Card.Title>Scale Control</Card.Title>
                  <Card.Text>
                    Advanced antiscalants and softening programs to prevent mineral deposits.
                  </Card.Text>
                  <Badge bg="primary">Essential</Badge>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={4} className="mb-4">
              <Card className="h-100">
                <Card.Body className="text-center">
                  <div className="feature-icon mb-3"><FaCog /></div>
                  <Card.Title>Corrosion Inhibition</Card.Title>
                  <Card.Text>
                    Oxygen scavengers and corrosion inhibitors to protect metal surfaces.
                  </Card.Text>
                  <Badge bg="danger">Critical</Badge>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={4} className="mb-4">
              <Card className="h-100">
                <Card.Body className="text-center">
                  <div className="feature-icon mb-3"><FaWater /></div>
                  <Card.Title>Condensate Treatment</Card.Title>
                  <Card.Text>
                    Neutralizing amines and filming amines for condensate line protection.
                  </Card.Text>
                  <Badge bg="info">Protection</Badge>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Treatment Chemicals */}
      <section className="section-padding">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold">Treatment Chemical Portfolio</h2>
              <p className="lead">Specialized chemicals for comprehensive boiler protection</p>
            </Col>
          </Row>
          <Row>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaFlask /></div>
                <h5>Oxygen Scavengers</h5>
                <p className="small">Sulfite, DEHA, and hydroquinone for dissolved oxygen removal</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaShieldAlt /></div>
                <h5>Corrosion Inhibitors</h5>
                <p className="small">Phosphates, polymers, and filming agents for metal protection</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaThermometerHalf /></div>
                <h5>Antiscalants</h5>
                <p className="small">Phosphate and polymer programs for scale prevention</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaWater /></div>
                <h5>Antifoam Agents</h5>
                <p className="small">Silicone and polymer-based antifoams for foam control</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Treatment Areas */}
      <section className="section-padding bg-light">
        <Container>
          <Row className="mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-4">Treatment Areas</h2>
              <p className="lead">Complete steam system protection from feedwater to condensate</p>
            </Col>
          </Row>
          
          <Row>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaWater className="me-2" />Feedwater Treatment</h4>
              <ul>
                <li>External softening and filtration</li>
                <li>Deaeration and oxygen removal</li>
                <li>Pre-boiler chemical dosing</li>
                <li>pH adjustment and alkalinity control</li>
              </ul>
            </Col>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaFire className="me-2" />Boiler Water Treatment</h4>
              <ul>
                <li>Internal scale and corrosion control</li>
                <li>Blowdown optimization</li>
                <li>Boiler water chemistry monitoring</li>
                <li>Sludge conditioning and removal</li>
              </ul>
            </Col>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaThermometerHalf className="me-2" />Steam Treatment</h4>
              <ul>
                <li>Steam purity monitoring</li>
                <li>Carryover prevention</li>
                <li>Attemperation water treatment</li>
                <li>Steam quality optimization</li>
              </ul>
            </Col>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaShieldAlt className="me-2" />Condensate Treatment</h4>
              <ul>
                <li>pH elevation with neutralizing amines</li>
                <li>Corrosion protection with filming amines</li>
                <li>Condensate polishing</li>
                <li>Return line protection</li>
              </ul>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Monitoring & Control */}
      <section className="section-padding">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold">Monitoring & Control Systems</h2>
              <p className="lead">Advanced monitoring for optimal treatment performance</p>
            </Col>
          </Row>
          <Row>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaChartLine /></div>
                <h5>Online Monitoring</h5>
                <p className="small">Real-time parameter tracking with automated alarms and controls</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaTools /></div>
                <h5>Automated Dosing</h5>
                <p className="small">Precise chemical dosing with feedback control systems</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaCheckCircle /></div>
                <h5>Water Analysis</h5>
                <p className="small">Comprehensive laboratory testing and analysis services</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaCog /></div>
                <h5>Performance Reporting</h5>
                <p className="small">Detailed performance reports and trend analysis</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Boiler Types */}
      <section className="section-padding bg-light">
        <Container>
          <Row className="mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-4">Boiler Type Solutions</h2>
              <p className="lead">Specialized treatment programs for different boiler configurations</p>
            </Col>
          </Row>
          
          <Row>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaFire className="me-2" />Fire Tube Boilers</h4>
              <ul>
                <li>Low-pressure boiler treatment programs</li>
                <li>Simple chemical dosing systems</li>
                <li>Cost-effective treatment solutions</li>
                <li>Manual monitoring options available</li>
              </ul>
            </Col>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaThermometerHalf className="me-2" />Water Tube Boilers</h4>
              <ul>
                <li>High-pressure boiler treatment programs</li>
                <li>Advanced chemical formulations</li>
                <li>Automated control systems</li>
                <li>Comprehensive monitoring solutions</li>
              </ul>
            </Col>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaCog className="me-2" />Package Boilers</h4>
              <ul>
                <li>Compact treatment solutions</li>
                <li>Integrated dosing systems</li>
                <li>Space-efficient equipment</li>
                <li>Quick installation and startup</li>
              </ul>
            </Col>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaIndustry className="me-2" />Industrial Boilers</h4>
              <ul>
                <li>Custom treatment programs</li>
                <li>Large-scale chemical systems</li>
                <li>Advanced monitoring capabilities</li>
                <li>24/7 technical support</li>
              </ul>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Technical Specifications */}
      <section className="section-padding">
        <Container>
          <Row className="mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-4">Technical Specifications</h2>
              <p className="lead">Treatment parameters and performance standards</p>
            </Col>
          </Row>
          
          <Row>
            <Col lg={6}>
              <h4 className="mb-3">Water Quality Parameters:</h4>
              <ul>
                <li>pH: 10.5-11.5 (boiler water)</li>
                <li>Conductivity: As per boiler pressure</li>
                <li>Dissolved oxygen: &lt;0.005 ppm</li>
                <li>Hardness: &lt;1 ppm as CaCO3</li>
                <li>Iron: &lt;0.1 ppm</li>
              </ul>
            </Col>
            <Col lg={6}>
              <h4 className="mb-3">Treatment Equipment:</h4>
              <ul>
                <li>Chemical dosing pumps and tanks</li>
                <li>Automatic blowdown controllers</li>
                <p className="small">Online monitoring instrumentation</p>
                <li>Water softening and filtration systems</li>
                <li>Deaeration equipment</li>
              </ul>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Call to Action */}
      <section className="section-padding bg-primary text-white">
        <Container>
          <Row className="text-center">
            <Col lg={8} className="mx-auto">
              <h2 className="display-5 fw-bold mb-4">Need Boiler Water Treatment Solutions?</h2>
              <p className="lead mb-4">
                Contact us for a comprehensive boiler system assessment and customized treatment program.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Button variant="warning" size="lg" className="cta-button">
                  System Assessment
                </Button>
                <Button variant="outline-light" size="lg">
                  Chemical Program Design
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default BoilerWaterTreatment;
